/**
 * L&G / Auralys — Theme JS
 * Minimaler Custom-Stack. Keine externen Dependencies.
 */
(() => {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ═══ Utilities ═══
  // Übersetzte Strings + Shop-Konfiguration kommen aus layout/theme.liquid (window.themeStrings)
  const t = Object.assign({
    empty: 'Dein Warenkorb ist leer.',
    emptyCta: 'Auralys entdecken',
    emptyCtaUrl: '/products/auralys-for-youth-face-elixier',
    quantity: 'Menge',
    decrease: 'Weniger',
    increase: 'Mehr',
    remove: 'Entfernen',
    itemsCount: '[count] Artikel im Warenkorb',
    moneyFormat: '€{{amount_with_comma_separator}}'
  }, window.themeStrings || {});

  function formatMoney(cents) {
    const amount = cents / 100;
    const fmt = t.moneyFormat;
    let value;
    if (fmt.indexOf('amount_no_decimals') !== -1) {
      value = Math.round(amount).toString();
    } else if (fmt.indexOf('amount_with_comma_separator') !== -1) {
      value = amount.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    } else {
      value = amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return fmt.replace(/\{\{\s*amount[a-z_]*\s*\}\}/, value);
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // ═══ Header scroll state ═══
  const header = document.querySelector('[data-header], #header');
  if (header) {
    let scrollTicking = false;
    const updateHeader = () => {
      const y = window.scrollY;
      header.dataset.scrolled = y > 8 ? 'true' : 'false';
      header.style.boxShadow = y > 20 ? '0 1px 0 rgba(0,0,0,0.04)' : 'none';
      scrollTicking = false;
    };
    window.addEventListener('scroll', () => {
      if (!scrollTicking) {
        requestAnimationFrame(updateHeader);
        scrollTicking = true;
      }
    }, { passive: true });
    updateHeader();
  }

  // ═══ Mobile Nav Drawer ═══
  const mobileNav = document.querySelector('[data-mobile-nav]');
  const mobileToggle = document.querySelector('[data-mobile-nav-toggle]');
  const mobileClose = document.querySelector('[data-mobile-nav-close]');
  const mobileBackdrop = document.querySelector('[data-mobile-nav-backdrop]');

  const openMobileNav = () => {
    if (!mobileNav) return;
    mobileNav.hidden = false;
    // Reflow erzwingen statt requestAnimationFrame: rAF feuert in Hintergrund-Tabs nicht,
    // dann bliebe der Drawer unsichtbar und würde alle Taps blocken.
    void mobileNav.offsetWidth;
    mobileNav.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';
    if (mobileToggle) mobileToggle.setAttribute('aria-expanded', 'true');
  };
  const closeMobileNav = () => {
    if (!mobileNav) return;
    mobileNav.classList.remove('is-open');
    setTimeout(() => { mobileNav.hidden = true; }, 280);
    document.documentElement.style.overflow = '';
    if (mobileToggle) mobileToggle.setAttribute('aria-expanded', 'false');
  };

  mobileToggle && mobileToggle.addEventListener('click', openMobileNav);
  mobileClose && mobileClose.addEventListener('click', closeMobileNav);
  mobileBackdrop && mobileBackdrop.addEventListener('click', closeMobileNav);
  document.addEventListener('keydown', (e) => {
    // Offene Sprachliste im Menü zuerst schließen lassen (Handler unten), erst das nächste Escape schließt das Menü
    if (e.key === 'Escape' && mobileNav && !mobileNav.hidden && !mobileNav.querySelector('[data-lang-switch][open]')) closeMobileNav();
  });

  // ═══ Sprachwahl (Dropdown) ═══
  // <details> klappt von selbst; hier nur: Escape und Klick daneben schließen.
  // Bei jedem Event frisch abfragen — der Header wird im Theme-Editor neu geladen.
  document.addEventListener('click', (e) => {
    document.querySelectorAll('[data-lang-switch][open]').forEach((el) => {
      if (!el.contains(e.target)) el.open = false;
    });
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('[data-lang-switch][open]').forEach((el) => {
      el.open = false;
      if (el.contains(document.activeElement)) el.querySelector('summary').focus();
    });
  });

  // ═══ Cart drawer ═══
  const cartDrawer = document.querySelector('[data-cart-drawer]');
  const cartBackdrop = document.querySelector('[data-cart-backdrop]');

  const openCart = (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (!cartDrawer) return;
    cartDrawer.hidden = false;
    void cartDrawer.offsetWidth; // Reflow statt rAF, siehe openMobileNav
    cartDrawer.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';
  };
  const closeCart = () => {
    if (!cartDrawer) return;
    cartDrawer.classList.remove('is-open');
    setTimeout(() => { cartDrawer.hidden = true; }, 260);
    document.documentElement.style.overflow = '';
  };

  document.querySelectorAll('[data-cart-toggle]').forEach(el => {
    el.addEventListener('click', openCart);
  });
  // Alle statischen Close-Trigger (Header-X + „Weiter stöbern" im Footer)
  document.querySelectorAll('[data-cart-close]').forEach(el => {
    el.addEventListener('click', (e) => { e.preventDefault(); closeCart(); });
  });
  cartBackdrop && cartBackdrop.addEventListener('click', closeCart);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && cartDrawer && !cartDrawer.hidden) closeCart();
  });

  // ═══ Cart-State-Renderer ═══
  function renderCartBody(cart) {
    const body = cartDrawer && cartDrawer.querySelector('[data-cart-body]');
    const footer = cartDrawer && cartDrawer.querySelector('[data-cart-footer]');
    if (!body) return;

    if (cart.item_count === 0) {
      body.innerHTML = '<div class="cart-drawer__empty-state">' +
        '<p class="cart-drawer__empty">' + escapeHtml(t.empty) + '</p>' +
        '<a class="btn btn-primary" href="' + escapeHtml(t.emptyCtaUrl) + '" data-cart-close>' + escapeHtml(t.emptyCta) + '</a>' +
        '</div>';
      if (footer) footer.hidden = true;
    } else {
      let html = '<ul class="cart-drawer__lines">';
      cart.items.forEach(item => {
        const title = escapeHtml(item.product_title);
        // wie im Liquid-Markup: 160 px breite Fassung statt des Originals
        const imgSrc = item.image ? item.image + (item.image.indexOf('?') === -1 ? '?' : '&') + 'width=160' : '';
        const img = imgSrc ? `<img src="${escapeHtml(imgSrc)}" alt="${title}" width="80" height="100" loading="lazy">` : '';
        html += `<li class="cart-drawer__line" data-line-key="${escapeHtml(item.key)}">
          <div class="cart-drawer__line-media">${img}</div>
          <div class="cart-drawer__line-info">
            <a class="cart-drawer__line-title" href="${escapeHtml(item.url)}">${title}</a>
            ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="cart-drawer__line-variant">${escapeHtml(item.variant_title)}</div>` : ''}
            ${item.selling_plan_allocation ? `<div class="cart-drawer__line-variant">${escapeHtml(item.selling_plan_allocation.selling_plan.name)}</div>` : ''}
            <div class="cart-drawer__line-controls">
              <div class="cart-drawer__qty" role="group" aria-label="${escapeHtml(t.quantity)}">
                <button type="button" class="cart-drawer__qty-btn" data-qty-change="${escapeHtml(item.key)}" data-qty-delta="-1" aria-label="${escapeHtml(t.decrease)}">−</button>
                <span class="cart-drawer__qty-val" data-qty-val>${item.quantity}</span>
                <button type="button" class="cart-drawer__qty-btn" data-qty-change="${escapeHtml(item.key)}" data-qty-delta="1" aria-label="${escapeHtml(t.increase)}">+</button>
              </div>
              <button type="button" class="cart-drawer__line-remove" data-qty-change="${escapeHtml(item.key)}" data-qty-set="0" aria-label="${escapeHtml(t.remove)}">${escapeHtml(t.remove)}</button>
            </div>
          </div>
          <div class="cart-drawer__line-price">${formatMoney(item.final_line_price)}</div>
        </li>`;
      });
      html += '</ul>';
      body.innerHTML = html;

      if (footer) {
        footer.hidden = false;
        const subtotalEl = footer.querySelector('[data-cart-subtotal]');
        if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
      }
    }

    // Update counts everywhere
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = cart.item_count;
      el.classList.toggle('is-hidden', cart.item_count === 0);
    });
    document.querySelectorAll('[data-cart-count-sr]').forEach(el => {
      el.textContent = t.itemsCount.replace('[count]', cart.item_count);
    });

    // Sticky-Cart
    const stickyCart = document.querySelector('[data-sticky-cart]');
    if (stickyCart) {
      stickyCart.hidden = cart.item_count === 0;
      const stickyCount = stickyCart.querySelector('[data-sticky-cart-count]');
      const stickyPrice = stickyCart.querySelector('[data-sticky-cart-price]');
      if (stickyCount) stickyCount.textContent = cart.item_count;
      if (stickyPrice) stickyPrice.textContent = formatMoney(cart.total_price);
    }

    // Re-attach close buttons in empty state
    body.querySelectorAll('[data-cart-close]').forEach(el => {
      if (el.tagName !== 'BUTTON' && el.tagName !== 'A') return;
      el.addEventListener('click', (e) => {
        if (el.tagName === 'BUTTON') { e.preventDefault(); closeCart(); }
      });
    });

    // Attach qty-change handlers
    body.querySelectorAll('[data-qty-change]').forEach(btn => {
      btn.addEventListener('click', handleQtyChange);
    });
  }

  // Sprachabhängige Cart-Routen aus dem Layout (Shopify Markets: /en/cart/add …), Fallback ohne Präfix
  const ts = window.themeStrings || {};
  const cartRoutes = {
    cart: ts.cartUrl || '/cart',
    add: ts.cartAddUrl || '/cart/add',
    change: ts.cartChangeUrl || '/cart/change'
  };

  // Verhindert überlappende /cart/change-Requests (Doppelklick auf +/− verliert sonst Inkremente)
  let cartRequestPending = false;

  function handleQtyChange(e) {
    const btn = e.currentTarget;
    if (cartRequestPending) return;
    const lineKey = btn.getAttribute('data-qty-change');
    const delta = parseInt(btn.getAttribute('data-qty-delta') || '0', 10);
    const absoluteSet = btn.getAttribute('data-qty-set');
    const lineEl = btn.closest('[data-line-key]');
    let newQty;
    if (absoluteSet !== null) {
      newQty = parseInt(absoluteSet, 10);
    } else {
      const curQty = parseInt(lineEl.querySelector('[data-qty-val]').textContent, 10) || 1;
      newQty = curQty + delta;
    }
    if (newQty < 0) newQty = 0;
    updateCartLine(lineKey, newQty);
  }

  async function fetchCart() {
    const res = await fetch(cartRoutes.cart + '.js', { headers: { 'Accept': 'application/json' } });
    if (!res.ok) throw new Error('cart fetch failed: ' + res.status);
    return res.json();
  }

  async function updateCartLine(key, quantity) {
    cartRequestPending = true;
    const buttons = cartDrawer ? cartDrawer.querySelectorAll('[data-qty-change]') : [];
    buttons.forEach(b => { b.disabled = true; });
    try {
      const res = await fetch(cartRoutes.change + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      });
      if (!res.ok) throw new Error('cart change failed: ' + res.status);
      const cart = await res.json();
      renderCartBody(cart);
      // Auf /cart zeigt die Seite hinter dem Drawer dieselben Zeilen — sonst stünden dort alte Werte
      if (document.body.classList.contains('template-cart')) { window.location.reload(); return; }
    } catch (err) {
      console.warn('cart/change failed', err);
      // Anzeige mit Server-Stand re-synchronisieren statt lautlos hängen zu bleiben
      try { renderCartBody(await fetchCart()); } catch (e2) { /* offline — nichts zu tun */ }
    } finally {
      cartRequestPending = false;
      buttons.forEach(b => { b.disabled = false; });
    }
  }

  async function addToCart(formData, cta) {
    if (cartRequestPending) return;
    cartRequestPending = true;
    try {
      if (cta) { cta.classList.add('is-loading'); cta.disabled = true; }
      const res = await fetch(cartRoutes.add + '.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });
      if (!res.ok) throw new Error('add failed');
      const cart = await fetchCart();
      renderCartBody(cart);
      openCart();
    } catch (err) {
      console.warn('cart/add failed', err);
      // Fallback: hard submit
      if (cta && cta.form) { cta.disabled = false; cta.form.submit(); }
    } finally {
      cartRequestPending = false;
      if (cta) { cta.classList.remove('is-loading'); cta.disabled = false; }
    }
  }

  // ═══ PDP Add-to-Cart Intercept ═══
  document.querySelectorAll('form[action*="/cart/add"]').forEach(form => {
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const cta = form.querySelector('[data-atc], [name="add"]');
      addToCart(new FormData(form), cta);
    });
  });

  // ═══ Initial render (on load) ═══
  if (cartDrawer) {
    fetchCart().then(renderCartBody).catch(() => {});
  }

  if (reducedMotion) return;

  // ═══ Hero-Parallax ═══
  const hero = document.querySelector('.hero-cosmic');
  if (hero) {
    const heroBg = hero.querySelector('.hero-bg, .hero-bg-img');
    const sparkles = hero.querySelectorAll('.sparkle');
    let pTicking = false;
    const updateParallax = () => {
      const rect = hero.getBoundingClientRect();
      if (rect.bottom >= 0 && rect.top <= window.innerHeight) {
        const scrollY = Math.max(0, -rect.top);
        if (heroBg) heroBg.style.transform = `scale(1.04) translate3d(0, ${scrollY * 0.15}px, 0)`;
        sparkles.forEach((s, i) => {
          const factor = 0.2 + (i % 5) * 0.05;
          s.style.setProperty('--parallax', `${scrollY * factor}px`);
        });
      }
      pTicking = false;
    };
    window.addEventListener('scroll', () => {
      if (!pTicking) {
        requestAnimationFrame(updateParallax);
        pTicking = true;
      }
    }, { passive: true });
  }

  // ═══ Sacred-Geometry-Reveal auf Pillars ═══
  const pillars = document.querySelectorAll('.pillar');
  if (pillars.length && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('pillar--revealed');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.4 });
    pillars.forEach((p, i) => {
      p.style.setProperty('--reveal-delay', `${i * 180}ms`);
      observer.observe(p);
    });
  }
})();
